/**
 * Durable storage for photos that haven't reached the server yet.
 *
 * Without this, the upload queue lives only in the page's memory: swiping the app
 * away from recents destroys the JavaScript context, aborts in-flight requests and
 * loses every queued photo. Tested — five captured, app killed, zero arrived.
 *
 * So each photo's bytes go into IndexedDB the moment it is taken, and are deleted
 * only once the server has acknowledged it. Anything still here at launch is
 * resumed. That survives app kill, a flat battery, and losing signal in a
 * basement — which is the actual condition these photos are taken in.
 *
 * Deliberately not localStorage: it is synchronous, ~5 MB, and strings only.
 * Photos are megabytes of binary.
 */
const DB_NAME = 'capturify-uploads';
const DB_VERSION = 1;
const STORE = 'pending';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') { reject(new Error('IndexedDB unavailable')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    try { result = fn(store); } catch (err) { reject(err); return; }
    t.oncomplete = () => resolve(result && result.result !== undefined ? result.result : result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/**
 * Every call is best-effort. Private browsing, a full disk or a browser without
 * IndexedDB must degrade to today's in-memory behaviour rather than stop a
 * technician taking photos.
 */
async function safe(label, fn, fallback = null) {
  try {
    return await fn();
  } catch (err) {
    console.warn(`[uploadStore] ${label} failed; uploads will not survive an app restart`, err);
    return fallback;
  }
}

/** Persist a photo and everything needed to upload it later without the UI. */
export function put(record) {
  return safe('put', () => tx('readwrite', (s) => s.put(record)));
}

export function remove(id) {
  return safe('remove', () => tx('readwrite', (s) => s.delete(id)));
}

export function all() {
  return safe('all', () => tx('readonly', (s) => new Promise((resolve, reject) => {
    const req = s.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  })), []);
}

export function clearAll() {
  return safe('clearAll', () => tx('readwrite', (s) => s.clear()));
}

export const isSupported = typeof indexedDB !== 'undefined';
